import { useState } from 'react';
import usePageTitle from '../hooks/usePageTitle';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Trash2, Plus } from 'lucide-react';
import { getBalanceSheet, createEntry, updateEntry, deleteEntry } from '../api/balanceSheet';
import Modal from '../components/Modal';
import Pagination from '../components/Pagination';
import FilterBar from '../components/FilterBar';
import { useAuth } from '../context/AuthContext';
import { canWrite } from '../utils/permissions';
import { useTranslation } from 'react-i18next';

// Must match ExpenseCategory.allowedFor on the backend.
const CATEGORIES = {
  DEBIT:  ['PACKAGING', 'DELIVERY', 'SOURCING', 'MARKETING', 'OTHER'],
  CREDIT: ['SALE', 'INVESTMENT', 'LOAN', 'REFUND', 'OTHER'],
};
const ALL_CATEGORIES = [...new Set([...CATEGORIES.DEBIT, ...CATEGORIES.CREDIT])];

const CATEGORY_STYLE = {
  PACKAGING:  'bg-blue-50 text-blue-700',
  DELIVERY:   'bg-purple-50 text-purple-700',
  SOURCING:   'bg-orange-50 text-orange-700',
  MARKETING:  'bg-pink-50 text-pink-700',
  OTHER:      'bg-gray-100 text-gray-600',
  SALE:       'bg-emerald-50 text-emerald-700',
  INVESTMENT: 'bg-teal-50 text-teal-700',
  LOAN:       'bg-amber-50 text-amber-700',
  REFUND:     'bg-cyan-50 text-cyan-700',
};

const SOURCE_STYLE = {
  COMPLETED_SALE:  'bg-green-100 text-green-800',
  ADVANCE_PAYMENT: 'bg-sky-100 text-sky-800',
};

const SALES_KEY = 'balance-sheet-sales';

function readSalesToggles() {
  try {
    const saved = JSON.parse(localStorage.getItem(SALES_KEY) || '{}');
    return { completedSales: !!saved.completedSales, advancePayments: !!saved.advancePayments };
  } catch {
    return { completedSales: false, advancePayments: false };
  }
}

const today = () => new Date().toISOString().split('T')[0];

const EMPTY_FORM = { entryType: 'DEBIT', category: 'OTHER', amount: '', description: '', expenseDate: '' };

const money = (n) => Number(n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2 });

function KindBadge({ row }) {
  const { t } = useTranslation();
  if (row.source !== 'MANUAL') {
    return (
      <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${SOURCE_STYLE[row.source]}`}>
        {t(`balanceSheet.${row.source}`)}
      </span>
    );
  }
  return (
    <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${CATEGORY_STYLE[row.category]}`}>
      {t(`expenseCategories.${row.category}`)}
    </span>
  );
}

function SummaryCard({ label, value, tone }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl px-4 py-3">
      <p className="text-xs text-gray-500">{label}</p>
      <p className={`text-lg font-semibold mt-0.5 ${tone}`}>৳{money(value)}</p>
    </div>
  );
}

export default function BalanceSheet() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [sales, setSales] = useState(readSalesToggles);

  usePageTitle('Balance Sheet');
  const { t } = useTranslation();
  const { user } = useAuth();
  const write = canWrite(user?.role);
  const [filters, setFilters] = useState({});
  const handleFilter = (key, val) => {
    setFilters((f) => {
      const next = { ...f, [key]: val || undefined };
      // A category from the other direction would match nothing — drop it.
      if (key === 'entryType' && next.category && val && !CATEGORIES[val].includes(next.category)) next.category = undefined;
      return next;
    });
    setPage(0);
  };
  const clearFilters = () => { setFilters({}); setPage(0); };

  const toggleSales = (key) => {
    setSales((s) => {
      const next = { ...s, [key]: !s[key] };
      try { localStorage.setItem(SALES_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
    setPage(0);
  };

  const query = { ...filters, ...sales };
  const { data, isLoading } = useQuery({
    queryKey: ['balance-sheet', page, query],
    queryFn: () => getBalanceSheet(page, 10, query).then((r) => r.data.data),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['balance-sheet'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const saveMutation = useMutation({
    mutationFn: (payload) =>
      modal?.mode === 'edit' ? updateEntry(modal.data.id, payload) : createEntry(payload),
    onSuccess: () => { invalidate(); closeModal(); },
    onError: (err) => setFormError(err.response?.data?.message || 'Something went wrong.'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => deleteEntry(id),
    onSuccess: () => { invalidate(); setDeleteTarget(null); },
  });

  const openCreate = () => {
    setForm({ ...EMPTY_FORM, expenseDate: today() });
    setFormError('');
    setModal({ mode: 'create' });
  };

  const openEdit = (row) => {
    setForm({
      entryType: row.entryType,
      category: row.category,
      amount: row.amount,
      description: row.description || '',
      expenseDate: row.entryDate,
    });
    setFormError('');
    setModal({ mode: 'edit', data: row });
  };

  const closeModal = () => setModal(null);

  const setEntryType = (entryType) => {
    setForm((f) => ({
      ...f,
      entryType,
      category: CATEGORIES[entryType].includes(f.category) ? f.category : 'OTHER',
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError('');
    saveMutation.mutate({
      entryType: form.entryType,
      category: form.category,
      amount: parseFloat(form.amount),
      description: form.description || null,
      expenseDate: form.expenseDate,
    });
  };

  const rows = data?.entries?.content ?? [];
  const totalPages = data?.entries?.totalPages ?? 0;
  const totals = data?.totals;

  const pageCredit = rows.filter((r) => r.entryType === 'CREDIT').reduce((sum, r) => sum + Number(r.amount), 0);
  const pageDebit = rows.filter((r) => r.entryType === 'DEBIT').reduce((sum, r) => sum + Number(r.amount), 0);

  const categoryOptions = (filters.entryType ? CATEGORIES[filters.entryType] : ALL_CATEGORIES)
    .map((c) => ({ value: c, label: t(`expenseCategories.${c}`) }));

  const filterFields = [
    { key: 'entryType', type: 'select', label: t('balanceSheet.allTypes'), options: [
      { value: 'CREDIT', label: t('balanceSheet.credit') },
      { value: 'DEBIT', label: t('balanceSheet.debit') },
    ] },
    { key: 'category', type: 'select', label: t('balanceSheet.allCategories'), options: categoryOptions },
    { key: 'dateFrom', type: 'date', label: t('balanceSheet.from') },
    { key: 'dateTo', type: 'date', label: t('balanceSheet.to') },
  ];

  const balance = Number(totals?.balance ?? 0);

  return (
    <div className="p-6 max-w-5xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">{t('balanceSheet.title')}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{data?.entries?.totalElements ?? 0} {t('common.total')}</p>
        </div>
        {write && (
          <button onClick={openCreate} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
            <Plus size={15} /> {t('balanceSheet.add')}
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        <SummaryCard label={t('balanceSheet.creditHint')} value={totals?.totalCredit} tone="text-emerald-700" />
        <SummaryCard label={t('balanceSheet.debitHint')} value={totals?.totalDebit} tone="text-red-600" />
        <SummaryCard label={t('balanceSheet.balance')} value={totals?.balance} tone={balance >= 0 ? 'text-gray-900' : 'text-red-600'} />
      </div>

      <FilterBar fields={filterFields} values={filters} onChange={handleFilter} onClear={clearFilters} />

      <div className="flex flex-wrap items-center gap-4 mb-4 -mt-1">
        {['completedSales', 'advancePayments'].map((key) => (
          <label key={key} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
            <input type="checkbox" checked={sales[key]} onChange={() => toggleSales(key)}
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
            {t(`balanceSheet.${key}`)}
          </label>
        ))}
        {(sales.completedSales || sales.advancePayments) && (
          <span className="text-xs text-gray-400">{t('balanceSheet.salesHint')}</span>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="py-16 text-center text-sm text-gray-400">{t('common.loading')}</div>
        ) : rows.length === 0 ? (
          <div className="py-16 text-center text-sm text-gray-400">{t('balanceSheet.noEntries')}</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50 text-left">
                <th className="px-4 py-3 font-medium text-gray-500">{t('common.date')}</th>
                <th className="px-4 py-3 font-medium text-gray-500">{t('common.category')}</th>
                <th className="px-4 py-3 font-medium text-gray-500">{t('common.description')}</th>
                <th className="px-4 py-3 font-medium text-gray-500 text-right">{t('balanceSheet.credit')}</th>
                <th className="px-4 py-3 font-medium text-gray-500 text-right">{t('balanceSheet.debit')}</th>
                <th className="px-4 py-3 w-20" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((row) => {
                const manual = row.source === 'MANUAL';
                return (
                  <tr key={`${row.source}-${row.id}`} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                      {new Date(row.entryDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </td>
                    <td className="px-4 py-3"><KindBadge row={row} /></td>
                    <td className="px-4 py-3 text-gray-500">
                      {manual
                        ? (row.description ?? <span className="text-gray-300">—</span>)
                        : <>{t('balanceSheet.order')} <span className="font-mono text-xs">#{row.orderToken}</span> · {row.customerName}</>}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-emerald-700">
                      {row.entryType === 'CREDIT' ? `৳${money(row.amount)}` : ''}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-red-600">
                      {row.entryType === 'DEBIT' ? `৳${money(row.amount)}` : ''}
                    </td>
                    <td className="px-4 py-3">
                      {write && manual && (
                        <div className="flex items-center gap-1 justify-end">
                          <button onClick={() => openEdit(row)} className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"><Pencil size={14} /></button>
                          <button onClick={() => setDeleteTarget(row)} className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"><Trash2 size={14} /></button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t border-gray-200 bg-gray-50">
                <td colSpan={3} className="px-4 py-2 text-sm font-semibold text-gray-700 text-right">{t('common.pageTotal')}</td>
                <td className="px-4 py-2 text-right font-bold text-emerald-700">৳{money(pageCredit)}</td>
                <td className="px-4 py-2 text-right font-bold text-red-600">৳{money(pageDebit)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        )}
      </div>

      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />

      {modal && (
        <Modal title={modal.mode === 'create' ? t('balanceSheet.addEntry') : t('balanceSheet.editEntry')} onClose={closeModal}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-2 p-1 bg-gray-100 rounded-lg">
              {['CREDIT', 'DEBIT'].map((type) => (
                <button key={type} type="button" onClick={() => setEntryType(type)}
                  className={`py-2 text-sm font-medium rounded-md transition-colors ${
                    form.entryType === type
                      ? `bg-white shadow-sm ${type === 'CREDIT' ? 'text-emerald-700' : 'text-red-600'}`
                      : 'text-gray-500 hover:text-gray-700'
                  }`}>
                  {t(type === 'CREDIT' ? 'balanceSheet.creditHint' : 'balanceSheet.debitHint')}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.category')} <span className="text-red-500">*</span></label>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white">
                  {CATEGORIES[form.entryType].map((c) => <option key={c} value={c}>{t(`expenseCategories.${c}`)}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('balanceSheet.amountSymbol')} <span className="text-red-500">*</span></label>
                <input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  required min="0.01" step="0.01" autoFocus
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0.00" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.date')} <span className="text-red-500">*</span></label>
              <input type="date" value={form.expenseDate} onChange={(e) => setForm({ ...form, expenseDate: e.target.value })} required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.description')}</label>
              <input type="text" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t('common.optional')} />
            </div>
            {formError && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{formError}</p>}
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={closeModal} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors">{t('common.cancel')}</button>
              <button type="submit" disabled={saveMutation.isPending} className="px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition-colors">
                {saveMutation.isPending ? t('common.saving') : t('common.save')}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {deleteTarget && (
        <Modal title={t('balanceSheet.deleteEntry')} onClose={() => setDeleteTarget(null)}>
          <p className="text-sm text-gray-600">{t('balanceSheet.deleteConfirm')} <span className="font-medium text-gray-900">৳{money(deleteTarget.amount)}</span>? {t('common.cannotBeUndone')}</p>
          <div className="flex justify-end gap-2 mt-5">
            <button onClick={() => setDeleteTarget(null)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors">{t('common.cancel')}</button>
            <button onClick={() => deleteMutation.mutate(deleteTarget.id)} disabled={deleteMutation.isPending}
              className="px-4 py-2 text-sm font-medium bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white rounded-lg transition-colors">
              {deleteMutation.isPending ? t('common.deleting') : t('common.delete')}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
